import { SSO_EVENTS } from '../../../constants/sso.constants';

/**
 * Shared confirm-then-act flow for the two destructive actions an admin can
 * take on a member: entering their account through SSO, and restricting or
 * restoring their platform access.
 *
 * Extend via the Inherit pattern:
 *   class MyController extends Inherit(ClockoutController, UserActionsController)
 *
 * Hosts: ReportsController (Reports > Users table + detail panel) and
 * UserController (/users/user). Both had parallel implementations of these two
 * flows, and neither had a confirmation step, so `warning_shown` and
 * `warning_cancelled` were never reported to the audit log.
 *
 * ── What this owns ────────────────────────────────────────────────────────
 * The orchestration only: which audit event each transition reports, when the
 * confirmation opens and closes, and how a failed action leaves it. The state
 * itself — the `userActionConfirm` slice and its setter — lives in
 * UserActionsDelegate, per the MVC convention. **Both must be mixed in**; this
 * half calls `setUserActionConfirm` and `userActionConfirm` through late-bound
 * `this`, so a host that composes only the controller breaks at the first click.
 * `requestUserAction` guards for exactly that.
 *
 * Everything else stays with the host: busy flags (per-clientId maps in reports,
 * booleans on the user page), the post-action refresh, the toasts, and the
 * capability checks.
 *
 * ── What this deliberately does NOT own ───────────────────────────────────
 * Capability checks. `canOpenSso` is a GETTER on ReportsController and a METHOD
 * on UserController. A shared version would have to pick one, and a missed call
 * site would then pass a function where a boolean was expected — truthy — and
 * silently show the Open SSO button to an admin without `admin:sso:open`.
 *
 * The act itself. Reports resolves a boot and embeds it; the user page opens
 * /open-sso/:clientId in a new tab, where OpenSsoView resolves its OWN boot. If
 * both resolved one, a single click would mint two SSO sessions and write two
 * `admin.sso.session_opened` audit rows. Hosts implement `runUserAction`.
 *
 * ── Constraints this file must keep ───────────────────────────────────────
 * NO class fields, NO constructor — see UserActionsDelegate for why.
 *
 * NO module imports of antd, DataController or resolveSsoBoot. This mixin's own
 * suite instantiates it with a fake host and no DOM, and axios (which
 * DataController pulls in) is ESM that react-scripts does not transform — so a
 * module-level dependency here would make it untestable. The API is reached
 * through `this.data`, late-bound.
 *
 * Every member is prefixed `userAction*`. Inherit overwrites silently in three
 * directions — a mixin method shadows the base class, a later mixin shadows an
 * earlier one, and the host's own class body shadows both — with no warning. The
 * prefix guarantees no collision with `restrictUser`, `openSsoForUser`,
 * `handleOpenSso`, `handleToggleRestrict`, `hasScope`, `initialize`, or the
 * design-elements mixins that are composed after this one.
 */

/** The two actions this flow can confirm. */
export const USER_ACTIONS = {
    SSO: 'sso',
    RESTRICT: 'restrict',
};

class UserActionsController {
    /**
     * Open the confirmation for an action on a member.
     *
     * Reports `warning_shown` for SSO. Returns void, and the report is
     * fire-and-forget, for a reason that is easy to undo by accident: on the
     * user page the confirm handler calls `window.open`, which browsers block
     * unless it runs synchronously inside the click gesture. Awaiting anything
     * on the way there — including this event — loses the tab in Safari and
     * Firefox. Do not make this async.
     *
     * @param {'sso'|'restrict'} action
     * @param {string} clientId
     * @param {{name?: string}} [extra] Copy context.
     * @returns {void}
     */
    requestUserAction(action, clientId, extra = {}) {
        if (!action || !clientId) return;

        if (typeof this.setUserActionConfirm !== 'function') {
            // Inherit gives no compile-time contract: a host that mixed in this
            // controller without UserActionsDelegate would otherwise die on an
            // undefined call with no hint about what is missing.
            console.error('UserActionsController: host must also mix in UserActionsDelegate');
            return;
        }

        this.setUserActionConfirm({ action, clientId, submitting: false, ...extra });

        if (action === USER_ACTIONS.SSO) {
            this.data?.reportSsoEvent(this.data.aba, clientId, SSO_EVENTS.WARNING_SHOWN);
        }
    }

    /**
     * Dismiss the confirmation. Reports `warning_cancelled` for SSO, so the
     * audit log distinguishes an admin who thought better of it from one who
     * never opened the dialog.
     *
     * No-op while submitting: the request is already in flight and closing the
     * modal would strand it. Mirrors handleCloseReconcileModal.
     * @returns {void}
     */
    cancelUserAction() {
        const confirm = this.userActionConfirm;
        if (!confirm || confirm.submitting) return;

        if (confirm.action === USER_ACTIONS.SSO) {
            this.data?.reportSsoEvent(this.data.aba, confirm.clientId, SSO_EVENTS.WARNING_CANCELLED);
        }

        this.setUserActionConfirm(null);
    }

    /**
     * Run the confirmed action through the host's `runUserAction`, which is the
     * half that genuinely differs per page.
     *
     * On success the confirmation closes. On failure it stays open with
     * `submitting: false` so the admin can retry or cancel — the host has
     * already shown the error. Same contract as ReconcileAccountModal.
     *
     * @returns {Promise<void>}
     */
    async userActionConfirmSubmit() {
        const confirm = this.userActionConfirm;
        if (!confirm || confirm.submitting) return;

        if (typeof this.runUserAction !== 'function') {
            // Inherit gives no compile-time contract, so a host that forgets
            // this would silently do nothing on confirm.
            console.error('UserActionsController: host must implement runUserAction(action, clientId, confirm)');
            return;
        }

        this.setUserActionConfirm({ submitting: true });
        try {
            const ok = await this.runUserAction(confirm.action, confirm.clientId, confirm);
            if (ok === false) {
                this.setUserActionConfirm({ submitting: false });
                return;
            }
            this.setUserActionConfirm(null);
        } catch (error) {
            // runUserAction owns error reporting; this only restores the modal.
            console.error('User action failed:', error);
            this.setUserActionConfirm({ submitting: false });
        }
    }
}

export default UserActionsController;
